import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shell = ({ children, testid }) => (
  <div data-testid={testid} className="min-h-screen bg-[#0A0D14] flex items-center justify-center px-5">
    <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#111723]/75 card-glow p-10 text-center">
      <div className="flex justify-center mb-8">
        <LogoMark className="w-14 h-14" />
      </div>
      {children}
    </div>
  </div>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) {
      setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {}
      if (++tries < 12) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [params]);

  return (
    <Shell testid="payment-success-page">
      {state === "loading" && (
        <>
          <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-6" />
          <h1 className="font-display text-2xl font-bold text-white mb-2">Vérification du paiement...</h1>
          <p className="text-sm text-slate-400" data-testid="payment-status-message">Un instant, nous confirmons votre règlement.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-6" />
          <h1 className="font-display text-2xl font-bold text-white mb-2">Paiement confirmé !</h1>
          <p className="text-sm text-slate-400 mb-8" data-testid="payment-status-message">
            Merci pour votre confiance. DIMORA vous recontacte sous 24h pour confirmer votre intervention.
          </p>
        </>
      )}
      {(state === "pending" || state === "error") && (
        <>
          <Loader2 className="w-12 h-12 text-amber-400 mx-auto mb-6" />
          <h1 className="font-display text-2xl font-bold text-white mb-2">Confirmation en cours</h1>
          <p className="text-sm text-slate-400 mb-8" data-testid="payment-status-message">
            Votre paiement est en cours de traitement. Une question ? Appelez le 06 18 90 74 35.
          </p>
        </>
      )}
      <Link
        to="/"
        data-testid="payment-home-link"
        className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3 text-sm font-bold text-[#0A0D14] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(242,193,78,0.4)]"
      >
        <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
      </Link>
    </Shell>
  );
};

export const PaymentCancel = () => (
  <Shell testid="payment-cancel-page">
    <XCircle className="w-12 h-12 text-amber-400 mx-auto mb-6" />
    <h1 className="font-display text-2xl font-bold text-white mb-2">Paiement annulé</h1>
    <p className="text-sm text-slate-400 mb-8">
      Aucun montant n'a été débité. Vous pouvez réessayer quand vous voulez, ou nous appeler au 06 18 90 74 35.
    </p>
    <div className="flex flex-col gap-3">
      <Link
        to="/#packs"
        data-testid="payment-retry-link"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-7 py-3 text-sm font-bold text-[#0A0D14] transition-all duration-300 hover:scale-[1.03]"
      >
        Réessayer
      </Link>
      <Link to="/" data-testid="payment-cancel-home-link" className="text-sm text-slate-400 hover:text-amber-300 transition-colors">
        Retour à l'accueil
      </Link>
    </div>
  </Shell>
);
